// /src/App.js — STAR 2026 GTM Math (final)
// Tabs:
// 1) STAR 2026 GTM Math – Budget & Forecast (Program Mix lives here)
// 2) STAR 2026 GTM Math – 12 Month Forecast
// 3) Assumptions & Scenarios (source of truth)

import React, { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  LineChart,
  Line,
} from "recharts";

/* ---------- INLINE CSS ---------- */
const INLINE_CSS = `
/* ========= Base ========= */
:root{
  --ink:#0b1a3c; --ink-2:#1f2a44; --muted:#6b7280; --line:#e5e7eb;
  --bg:#f7f8fa; --card:#fff; --brand:#22c55e; --demand:#3b82f6;
  --enable:#f59e0b; --intel:#8b5cf6; --accent:#485fc7;
  /* STAR cues */
  --star:#4B2C7A; --star-50:#f6f3ff; --star-100:#ede7ff;
}
*{box-sizing:border-box}
html,body{height:100%}
body{font-family:Inter,system-ui,Arial,sans-serif;background:var(--bg);color:#222}

/* ========= Shell ========= */
.container{max-width:1400px;margin:0 auto;padding:20px}
h1{font-size:1.8rem;text-align:center;margin:18px 0 24px;color:#0b1a3c}
h2{font-size:1.1rem;margin:8px 0;color:#1f2a44}
.small{font-size:.86rem;color:#64748b}

/* ========= Tabs ========= */
.tabs{display:flex;gap:8px;justify-content:center;flex-wrap:wrap;margin-bottom:10px}
.tab-btn{border:1px solid #cdd5e0;background:#fff;padding:8px 12px;border-radius:10px;cursor:pointer;font-weight:700;font-size:.95rem}
.tab-btn.active{background:var(--star);color:#fff;border-color:var(--star)}

/* ========= Cards / Tables ========= */
.table-block{background:#fff;border:1px solid var(--line);border-radius:12px;box-shadow:0 1px 4px rgba(0,0,0,.05);margin-bottom:16px;overflow-x:auto;padding:12px 16px}
.header-row{display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;padding:10px 12px;border-bottom:2px solid var(--star);background:linear-gradient(180deg,var(--star-50),#fff)}
.section-title{font-weight:800;font-size:1.05rem;color:#0b1a3c}
.purple .header-row{border-bottom-color:var(--intel);background:linear-gradient(180deg,#f5f3ff,#fff)}
.purple-band{background:var(--intel);border-radius:10px;padding:8px 12px;margin-bottom:10px}
.white{color:#fff}

/* Zebra rows */
.planning-table tbody tr:nth-child(odd){background:#fafafa}

/* ========= Forecast/Benchmark/Assumption Tables ========= */
.forecast-table,.benchmark-table,.planning-table,.assump-table{width:100%;border-collapse:collapse;font-size:.92rem}
.forecast-table th,.forecast-table td,
.benchmark-table th,.benchmark-table td,
.planning-table th,.planning-table td,
.assump-table th,.assump-table td{border:1px solid #dde3ea;padding:8px;vertical-align:top}
.forecast-table th,.assump-table thead th{background:var(--star);color:#fff}
.forecast-table th:first-child,.forecast-table td:first-child{background:#ffeb3b;color:#000;font-weight:700}
.forecast-table td{text-align:right}
.totals-header{background:#002855 !important;color:#fff}

/* ========= Grid Helpers ========= */
.grid{display:grid;gap:12px}
.two{grid-template-columns:1fr 1fr}
.three{grid-template-columns:repeat(3,1fr)}
.card{background:#fff;border:1px solid var(--line);border-radius:12px;padding:12px}

/* ========= Inputs ========= */
.input-row{display:flex;gap:10px;align-items:center;margin:8px 0}
.input-row label{min-width:220px;font-weight:700;color:#111827}
input[type="number"],input[type="text"],select{
  border:1px solid #cbd5e1;border-radius:10px;padding:10px 12px;min-height:40px;background:#fff;box-shadow:0 1px 0 rgba(0,0,0,.02)
}
input[type="number"]:focus,input[type="text"]:focus,select:focus{outline:2px solid var(--star-100);border-color:var(--star)}
input[type="range"]{width:200px}
.btn{border:1px solid var(--star);background:var(--star);color:#fff;padding:8px 12px;border-radius:10px;cursor:pointer;font-weight:700}
.btn.secondary{background:#fff;color:var(--star)}

/* ========= Toast ========= */
.toast{position:fixed;right:18px;bottom:18px;background:#0b1a3c;color:#fff;padding:12px 14px;border-radius:14px;box-shadow:0 10px 30px rgba(0,0,0,.25);max-width:380px;z-index:50}
.toast h4{margin:0 0 6px 0;font-size:.95rem}
.toast ul{margin:0;padding-left:18px;max-height:160px;overflow:auto}

/* ========= Program Mix Layout ========= */
.pmix{display:grid;grid-template-columns:520px 1fr;gap:14px;align-items:start}

/* Left panel: independent scroll + nice scrollbar */
.pmix .panel{
  max-height:calc(100vh - 180px);
  overflow:auto !important;
  position:sticky; top:84px; z-index:1; padding-right:10px;
  -webkit-overflow-scrolling:touch;
  background:linear-gradient(180deg,#fff,#fff) padding-box, linear-gradient(180deg,#fff,var(--star-50)) border-box;
  border:1px solid var(--line); border-radius:14px;
}
.pmix .panel::-webkit-scrollbar{ width:8px }
.pmix .panel::-webkit-scrollbar-thumb{ background:#cbd5e1; border-radius:8px }

/* Family cards with colored left rails */
.family{border:1px solid var(--line);border-radius:12px;padding:8px 10px;margin-bottom:12px;background:#fff}
.family h4{margin:2px 0 8px}
.family .row:nth-child(2n){background:#fafafa}
.family .row:hover{background:#f6f9ff}
.family.tone-brand{border-left:6px solid var(--brand); background:linear-gradient(180deg,#f0fdf4,#fff)}
.family.tone-demand{border-left:6px solid var(--demand); background:linear-gradient(180deg,#eff6ff,#fff)}
.family.tone-enable{border-left:6px solid var(--enable); background:linear-gradient(180deg,#fffbeb,#fff)}
.family.tone-intel{border-left:6px solid var(--intel); background:linear-gradient(180deg,#f5f3ff,#fff)}

.pmix .sub{color:#6b7280;font-size:.85rem;margin-left:4px}
.pmix .subtotal{text-align:right;font-weight:800;padding:10px 6px 2px}

/* Line-item rows (right column) */
.pmix .row{display:grid;grid-template-columns:22px 1fr 120px 120px 1fr 120px;align-items:center;gap:8px;padding:5px 0}
.pmix .row input[type="number"],.pmix .row input[type="text"]{width:100%}
.pmix .row .money{text-align:right;font-weight:800}

/* Sidebar (left) */
.sidebar-title{font-weight:900;color:#0b1a3c;margin:6px 0}
.sidebar-subtle{font-size:.85rem;color:#64748b;line-height:1.35;margin-bottom:10px;overflow-wrap:anywhere}

/* Label | input | unit grid */
.row-grid{display:grid;grid-template-columns:1fr 170px 88px;gap:10px 12px;align-items:center;margin:10px 0}
.row-grid .lbl{font-weight:700;font-size:.95rem;color:#111827;line-height:1.25}
.row-grid input[type="number"],.row-grid input[type="text"]{width:170px;border:1px solid #cbd5e1;border-radius:10px;padding:10px 12px;min-height:40px}
.row-grid input[type="number"]:focus,.row-grid input[type="text"]:focus{outline:2px solid var(--star-100);border-color:var(--star)}
.row-grid .unit{font-size:.82rem;color:#6b7280;white-space:nowrap;text-align:left}

/* Pills */
.badge{display:inline-block;padding:4px 8px;border-radius:999px;background:#eef2ff;color:#3730a3;font-weight:800;font-size:.78rem;margin-left:6px}

/* CSV uploader spacing */
.pmix .uploader { display:flex; gap:8px; align-items:center; margin:8px 0 6px; }
.pmix .uploader input[type="file"] { max-width: 260px; }

/* Responsive */
@media (max-width:1360px){
  .pmix{grid-template-columns:480px 1fr}
  .row-grid{grid-template-columns:1fr 170px}
  .row-grid .unit{grid-column:2 / 3;justify-self:end;margin-top:4px;text-align:right}
}
@media (max-width:1160px){
  .pmix{grid-template-columns:1fr}
}

/* Field set for Labeled() */
.fw-field{display:grid;grid-template-rows:auto auto;row-gap:6px;margin:10px 0}
.fw-label{font-weight:800;color:#0b1a3c;line-height:1.25;font-size:.95rem}
.fw-hint{margin-left:8px;padding:2px 8px;border-radius:999px;background:#eef2ff;color:#3730a3;font-weight:800;font-size:.75rem}
.fw-input-wrap{display:grid;grid-template-columns:1fr max-content;align-items:center;column-gap:10px}
.fw-input{width:100%;border:1px solid #cbd5e1;border-radius:10px;min-height:40px;padding:10px 12px;background:#fff;box-shadow:0 1px 0 rgba(0,0,0,.02)}
.fw-unit{white-space:nowrap;font-size:.82rem;color:#6b7280}
`;

/* ---------- Number helpers + user-friendly input ---------- */
const formatNumber = (v, maxFrac = 2) =>
  Number.isFinite(Number(v))
    ? Number(v).toLocaleString(undefined, { maximumFractionDigits: maxFrac })
    : "";

const parseNumber = (s) => {
  if (s == null) return 0;
  const n = Number(String(s).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

// Input that doesn't fight the user while typing; adds commas on blur.
function NumberField({
  value,
  onChange,
  maxFraction = 2,
  placeholder = "",
  width,
  ...rest
}) {
  const [draft, setDraft] = React.useState(
    value === undefined || value === null ? "" : String(value)
  );
  const prev = React.useRef(value);

  React.useEffect(() => {
    if (value !== prev.current) {
      prev.current = value;
      setDraft(value === undefined || value === null ? "" : String(value));
    }
  }, [value]);

  return (
    <input
      type="text"
      inputMode="decimal"
      value={draft}
      placeholder={placeholder}
      onChange={(e) => {
        const next = e.target.value.replace(/[^\d.,-]/g, "");
        setDraft(next);
      }}
      onBlur={() => {
        const n = parseNumber(draft);
        onChange?.(n);
        setDraft(formatNumber(n, maxFraction));
      }}
      onFocus={(e) => {
        e.target.setSelectionRange?.(0, e.target.value.length);
        setDraft(String(parseNumber(draft)));
      }}
      style={width ? { width } : undefined}
      {...rest}
    />
  );
}

/* ---------- Utilities ---------- */
const months = [
  "26-Jan",
  "26-Feb",
  "26-Mar",
  "26-Apr",
  "26-May",
  "26-Jun",
  "26-Jul",
  "26-Aug",
  "26-Sep",
  "26-Oct",
  "26-Nov",
  "26-Dec",
];

const fmt = (n, d = 1) =>
  Number(n ?? 0).toLocaleString(undefined, {
    maximumFractionDigits: d,
    minimumFractionDigits: d,
  });

const money = (n) =>
  (n ?? 0).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

/* ---------- Scenario presets ---------- */
const SCENARIOS = {
  Good: { convMult: 1.0, cplMult: 1.05 },
  Better: { convMult: 1.15, cplMult: 1.0 },
  Best: { convMult: 1.3, cplMult: 0.95 },
};

/* ---------- CSV helpers ---------- */
function parseCSV(text) {
  const rows = [];
  let row = [],
    field = "",
    inQ = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i],
      n = text[i + 1];
    if (c === `"` && inQ && n === `"`) {
      field += `"`;
      i++;
      continue;
    }
    if (c === `"`) {
      inQ = !inQ;
      continue;
    }
    if (c === `,` && !inQ) {
      row.push(field);
      field = "";
      continue;
    }
    if ((c === `\n` || c === `\r`) && !inQ) {
      if (field.length || row.length) {
        row.push(field);
        rows.push(row);
        row = [];
        field = "";
      }
      continue;
    }
    field += c;
  }
  if (field.length || row.length) {
    row.push(field);
    rows.push(row);
  }
  const header = (rows.shift() || []).map((h) => h.trim());
  return rows
    .filter((r) => r.some((v) => v && v.trim().length))
    .map((r) => {
      const o = {};
      header.forEach((h, i) => (o[h] = (r[i] ?? "").trim()));
      return o;
    });
}

const CSV_TEMPLATE = `assump_segment,ftes,assump_visitors_per_month,content_syndication_leads_per_month,q1_weight_pct,q2_weight_pct,q3_weight_pct,q4_weight_pct,scope_plans_1to1_per_q,scope_plans_1few_per_q,annual_enriched,rate_brand_cpm,rate_demand_cpl,rate_enrichment_per_contact,rate_enable_1to1,rate_enable_1few,rate_show_plan,rate_seq_1to1_per_acct_mo,rate_seq_1few_pct_mo,rate_market_intel_per_signal,rfp_leads_per_one_to_one,ae_show_plans_per_q,feather_show_plans_per_q,annual_display_budget
retail,1,2000,25,40,30,15,15,25,1,2500,15,41,1.0,25,250,100,10,0.10,0.10,1,,,$40000
ad,5,4000,100,40,30,15,15,125,5,12500,,,,,,,,,,,
ae,2,1500,,40,30,15,15,,3,20000,,,,,,,,,,,12,,
feather,5,,,40,30,15,15,,3,20000,,,,,,,,,,,,,12`;

function downloadCSVTemplate() {
  const blob = new Blob([CSV_TEMPLATE], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "gtm-math-template.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function diffLine(label, before, after, f = (x) => x) {
  if (before === after || Number(before) === Number(after)) return null;
  return `${label}: ${f(before)} → ${f(after)}`;
}

/* ============================= App ============================= */
export default function App() {
  const [tab, setTab] = useState("budget");
  const [scenario, setScenario] = useState("Good");

  /* Base benchmark rates + Global pricing */
  const [rates, setRates] = useState({
    visitorToMQL: 0.019,
    pixelToMQL: 0.05,
    mqlToSQL: 0.16,
    sqlToMeeting: 0.5,
    meetingToQualified: 0.75,
    qualifiedToWon: 0.3,
    avgDeal: 50000,
    brandCPM: 15,
    customContentAsset: 2500,
    demandCPL: 41,
    websiteFlatQuarter: 4000,
    enable1to1Unit: 25,
    enable1fewUnit: 250,
    showPlanUnit: 100,
    seq1to1PerAcctMo: 10,
    seq1fewPctPerMo: 0.1,
    enrichmentPerContact: 1.0,
    marketIntelPerSignal: 0.1,
  });

  /* Volumes */
  const [vol, setVol] = useState({
    retailVisitorsMo: 2000,
    adVisitorsMo: 4000,
    aeVisitorsMo: 1500,
  });

  /* Costs & plays (for narrative tables) */
  const [costs, setCosts] = useState({
    cplRetail: 60,
    cplADLow: 35,
    cplADHigh: 50,
    intelIntentPerQuarter: 2000,
    accountPlanPerPlay: 1500,
    contentSupportPerPlay: 1200,
    retailSyndPerMo: 25,
    adSyndPerMo: 100,
    retailOneToOnePerQ: 25,
    retailOneToFewPerQ: 1,
    adOneToOnePerRepPerQ: 10,
    adOneToFewPerRepPerQ: 5,
    aeOneToFewPerRepPerQ: 3,
  });

  /* Team & weights */
  const [team, setTeam] = useState({ retailReps: 1, adReps: 5, aeReps: 2 });
  const [weights, setWeights] = useState({ Q1: 40, Q2: 30, Q3: 15, Q4: 15 });

  /* Program Mix seed */
  const [pmixSeed, setPmixSeed] = useState({
    syndLeadsPerQ: 375,
    oneToOnePlans: 300,
    oneToFewPlans: 128,
    showPlans: 0,
    displayImpr: 2666667,
    version: 1,
  });

  /* Add-ons */
  const [addons, setAddons] = useState([]);

  /* RFP knobs */
  const [rfp, setRfp] = useState({
    retail: {
      oneToOnePerQ: 25,
      leadsPerOneToOne: 1,
      oneToFewPerQ: 1,
      annualEnriched: 2500,
    },
    ad: {
      oneToOnePerQ: 125,
      oneToFewPerQ: 5,
      syndLeadsPerQ: 125,
      annualEnriched: 12500,
    },
    ae: { oneToFewPerQ: 3, annualEnriched: 20000, showPlansPerQ: 12 },
    feather: { oneToFewPerQ: 3, annualEnriched: 20000, showPlansPerQ: 12 },
  });

  /* CSV import summary + toast */
  const [importSummary, setImportSummary] = useState([]);
  const [toast, setToast] = useState(null);

  /* ---------- Scenario-applied values ---------- */
  const scen = SCENARIOS[scenario];
  const applied = useMemo(() => {
    const r = {
      ...rates,
      visitorToMQL: rates.visitorToMQL * scen.convMult,
      mqlToSQL: rates.mqlToSQL * scen.convMult,
      sqlToMeeting: rates.sqlToMeeting * scen.convMult,
      meetingToQualified: rates.meetingToQualified * scen.convMult,
      qualifiedToWon: rates.qualifiedToWon * scen.convMult,
    };
    const c = {
      ...costs,
      cplRetail: costs.cplRetail * scen.cplMult,
      cplADLow: costs.cplADLow * scen.cplMult,
      cplADHigh: costs.cplADHigh * scen.cplMult,
    };
    return { r, c };
  }, [rates, costs, scen]);

  /* ---------- Funnel math ---------- */
  function funnelFromVisitors(vPerMo) {
    const v = vPerMo;
    const mql = v * applied.r.visitorToMQL;
    const sql = mql * applied.r.mqlToSQL;
    const meetings = sql * applied.r.sqlToMeeting;
    const qualified = meetings * applied.r.meetingToQualified;
    const won = qualified * applied.r.qualifiedToWon;
    const pipeline = qualified * applied.r.avgDeal;
    const revenue = won * applied.r.avgDeal;
    return { v, mql, sql, meetings, qualified, won, pipeline, revenue };
  }

  /* ---------- Budget helpers ---------- */
  function frontLoadAnnualToQuarters(annual) {
    const totalW = weights.Q1 + weights.Q2 + weights.Q3 + weights.Q4 || 100;
    return {
      Q126: annual * (weights.Q1 / totalW),
      Q226: annual * (weights.Q2 / totalW),
      Q326: annual * (weights.Q3 / totalW),
      Q426: annual * (weights.Q4 / totalW),
      annual,
    };
  }
  function monthlyToQuarterlyAnnual(monthly) {
    const annual = monthly * 12;
    return { annual, ...frontLoadAnnualToQuarters(annual) };
  }
  function addonsTotals() {
    const mo = addons
      .filter((a) => a.per === "month")
      .reduce((s, a) => s + a.amount, 0);
    const qr = addons
      .filter((a) => a.per === "quarter")
      .reduce((s, a) => s + a.amount, 0);
    const yr = addons
      .filter((a) => a.per === "year")
      .reduce((s, a) => s + a.amount, 0);
    const annual = mo * 12 + qr * 4 + yr;
    return { annual, ...frontLoadAnnualToQuarters(annual) };
  }

  /* ---------- Derived model (for narrative tables) ---------- */
  const model = useMemo(() => {
    const retailF = funnelFromVisitors(vol.retailVisitorsMo);
    const adF = funnelFromVisitors(vol.adVisitorsMo);
    const aeF = funnelFromVisitors(vol.aeVisitorsMo);

    const retailSynd = monthlyToQuarterlyAnnual(
      costs.retailSyndPerMo * applied.c.cplRetail
    );
    const adSynd = monthlyToQuarterlyAnnual(
      costs.adSyndPerMo * ((applied.c.cplADLow + applied.c.cplADHigh) / 2)
    );

    const retailPlaysAnnual =
      (costs.retailOneToOnePerQ + costs.retailOneToFewPerQ) *
      (costs.accountPlanPerPlay + costs.contentSupportPerPlay) *
      4;

    const adPlaysAnnual =
      team.adReps *
      (costs.adOneToOnePerRepPerQ + costs.adOneToFewPerRepPerQ) *
      (costs.accountPlanPerPlay + costs.contentSupportPerPlay) *
      4;

    const aePlaysAnnual =
      team.aeReps *
      costs.aeOneToFewPerRepPerQ *
      (costs.accountPlanPerPlay + costs.contentSupportPerPlay) *
      4;

    const intelAnnual =
      (team.retailReps + team.adReps + team.aeReps) *
      costs.intelIntentPerQuarter *
      4;

    return {
      retailF,
      adF,
      aeF,
      budgets: {
        retail: {
          contentSynd: frontLoadAnnualToQuarters(retailSynd.annual),
          plays: frontLoadAnnualToQuarters(retailPlaysAnnual),
        },
        ad: {
          contentSynd: frontLoadAnnualToQuarters(adSynd.annual),
          plays: frontLoadAnnualToQuarters(adPlaysAnnual),
        },
        ae: {
          contentSynd: { annual: 0, Q126: 0, Q226: 0, Q326: 0, Q426: 0 },
          plays: frontLoadAnnualToQuarters(aePlaysAnnual),
        },
        intel: frontLoadAnnualToQuarters(intelAnnual),
        addons: addonsTotals(),
      },
    };
  }, [vol, applied, team, costs, weights, addons]);

  /* ---------- CSV Upload (updates many states) ---------- */
  async function handleCsvUpload(file) {
    if (!file) return;
    const text = await file.text();
    const rows = parseCSV(text);

    const changes = [];
    let nextVol = { ...vol },
      nextCosts = { ...costs },
      nextTeam = { ...team },
      nextWeights = { ...weights };
    let nextRates = { ...rates };
    let seedUpdate = null;
    let nextRfp = { ...rfp };

    rows.forEach((row) => {
      const seg = (row.assump_segment || "").toLowerCase();
      const ftes = Number(row.ftes || 0) || 0;

      if (seg === "rates") {
        const map = {
          rate_brand_cpm: "brandCPM",
          rate_demand_cpl: "demandCPL",
          rate_enrichment_per_contact: "enrichmentPerContact",
          rate_enable_1to1: "enable1to1Unit",
          rate_enable_1few: "enable1fewUnit",
          rate_show_plan: "showPlanUnit",
          rate_seq_1to1_per_acct_mo: "seq1to1PerAcctMo",
          rate_seq_1few_pct_mo: "seq1fewPctPerMo",
          rate_market_intel_per_signal: "marketIntelPerSignal",
        };
        Object.entries(map).forEach(([csvKey, stateKey]) => {
          const v = row[csvKey];
          if (v !== undefined && v !== "") nextRates[stateKey] = Number(v);
        });

        const brandImprBudget = Number(row.annual_display_budget || 0);
        if (brandImprBudget) {
          const impr = Math.round(
            (brandImprBudget / (nextRates.brandCPM || 1)) * 1000
          );
          seedUpdate = (s) => ({
            ...s,
            displayImpr: impr,
            version: s.version + 1,
          });
          changes.push(
            `Program Mix: set Display Impressions ≈ ${impr.toLocaleString()}`
          );
        }

        const leadsPer1to1 = Number(row.rfp_leads_per_one_to_one || 0);
        if (leadsPer1to1) {
          const before = rfp.retail?.leadsPerOneToOne ?? 1;
          nextRfp.retail = {
            ...nextRfp.retail,
            leadsPerOneToOne: leadsPer1to1,
          };
          changes.push(
            diffLine("Leads per 1:1 account (per Q)", before, leadsPer1to1, fmt)
          );
        }

        const aeShows = Number(row.ae_show_plans_per_q || 0);
        const featherShows = Number(row.feather_show_plans_per_q || 0);
        if (aeShows) {
          const before = rfp.ae?.showPlansPerQ ?? 0;
          nextRfp.ae = { ...nextRfp.ae, showPlansPerQ: aeShows };
          changes.push(diffLine("AE Show Plans/Q", before, aeShows, fmt));
        }
        if (featherShows) {
          const before = rfp.feather?.showPlansPerQ ?? 0;
          nextRfp.feather = { ...nextRfp.feather, showPlansPerQ: featherShows };
          changes.push(
            diffLine("Featherlite Show Plans/Q", before, featherShows, fmt)
          );
        }
        return;
      }

      const vpm = Number(row.assump_visitors_per_month || 0);
      if (vpm) {
        if (seg === "retail") {
          changes.push(
            diffLine("Retail Visitors/Mo", vol.retailVisitorsMo, vpm, fmt)
          );
          nextVol.retailVisitorsMo = vpm;
        }
        if (seg === "ad") {
          changes.push(diffLine("AD Visitors/Mo", vol.adVisitorsMo, vpm, fmt));
          nextVol.adVisitorsMo = vpm;
        }
        if (seg === "ae") {
          changes.push(diffLine("AE Visitors/Mo", vol.aeVisitorsMo, vpm, fmt));
          nextVol.aeVisitorsMo = vpm;
        }
      }

      const oneToOneQ = Number(row.scope_plans_1to1_per_q || 0);
      const oneToFewQ = Number(row.scope_plans_1few_per_q || 0);

      if (seg === "retail") {
        if (oneToOneQ) {
          changes.push(
            diffLine(
              "Retail 1:1 Plays/Q",
              costs.retailOneToOnePerQ,
              oneToOneQ,
              fmt
            )
          );
          nextCosts.retailOneToOnePerQ = oneToOneQ;
        }
        if (oneToFewQ) {
          changes.push(
            diffLine(
              "Retail 1:Few Plays/Q",
              costs.retailOneToFewPerQ,
              oneToFewQ,
              fmt
            )
          );
          nextCosts.retailOneToFewPerQ = oneToFewQ;
        }
        if (ftes) {
          changes.push(diffLine("Retail FTEs", team.retailReps, ftes, fmt));
          nextTeam.retailReps = ftes;
        }
      }
      if (seg === "ad") {
        if (oneToOneQ && ftes > 0) {
          const perRep = Math.round((oneToOneQ / ftes) * 100) / 100;
          changes.push(
            diffLine(
              "AD 1:1 Plays/Rep/Q",
              costs.adOneToOnePerRepPerQ,
              perRep,
              fmt
            )
          );
          nextCosts.adOneToOnePerRepPerQ = perRep;
        }
        if (oneToFewQ && ftes > 0) {
          const perRep = Math.round((oneToFewQ / ftes) * 100) / 100;
          changes.push(
            diffLine(
              "AD 1:Few Plays/Rep/Q",
              costs.adOneToFewPerRepPerQ,
              perRep,
              fmt
            )
          );
          nextCosts.adOneToFewPerRepPerQ = perRep;
        }
        if (ftes) {
          changes.push(diffLine("AD FTEs", team.adReps, ftes, fmt));
          nextTeam.adReps = ftes;
        }
      }
      if (seg === "ae") {
        if (oneToFewQ && ftes > 0) {
          const perRep = Math.round((oneToFewQ / ftes) * 100) / 100;
          changes.push(
            diffLine(
              "AE 1:Few Plays/Rep/Q",
              costs.aeOneToFewPerRepPerQ,
              perRep,
              fmt
            )
          );
          nextCosts.aeOneToFewPerRepPerQ = perRep;
        }
        if (ftes) {
          changes.push(diffLine("AE FTEs", team.aeReps, ftes, fmt));
          nextTeam.aeReps = ftes;
        }
      }
      if (seg === "feather") {
        if (oneToFewQ) {
          const before = rfp.feather?.oneToFewPerQ ?? 0;
          nextRfp.feather = { ...nextRfp.feather, oneToFewPerQ: oneToFewQ };
          changes.push(
            diffLine("Featherlite 1:Few Plays/Q", before, oneToFewQ, fmt)
          );
        }
      }

      const synd = Number(row.content_syndication_leads_per_month || 0);
      if (synd) {
        if (seg === "retail") {
          changes.push(
            diffLine("Retail Synd/Month", costs.retailSyndPerMo, synd, fmt)
          );
          nextCosts.retailSyndPerMo = synd;
        }
        if (seg === "ad") {
          changes.push(diffLine("AD Synd/Month", costs.adSyndPerMo, synd, fmt));
          nextCosts.adSyndPerMo = synd;
        }
      }

      const w1 = Number(row.q1_weight_pct || 0),
        w2 = Number(row.q2_weight_pct || 0),
        w3 = Number(row.q3_weight_pct || 0),
        w4 = Number(row.q4_weight_pct || 0);
      if (w1 + w2 + w3 + w4 === 100) {
        const newW = { Q1: w1, Q2: w2, Q3: w3, Q4: w4 };
        changes.push(diffLine("Weights Q1", weights.Q1, newW.Q1, fmt));
        changes.push(diffLine("Weights Q2", weights.Q2, newW.Q2, fmt));
        changes.push(diffLine("Weights Q3", weights.Q3, newW.Q3, fmt));
        changes.push(diffLine("Weights Q4", weights.Q4, newW.Q4, fmt));
        nextWeights = newW;
      }

      const annualEnriched = Number(row.annual_enriched || 0);
      if (annualEnriched && ["retail", "ad", "ae", "feather"].includes(seg)) {
        const before = rfp[seg]?.annualEnriched ?? 0;
        nextRfp[seg] = { ...nextRfp[seg], annualEnriched };
        changes.push(
          diffLine(
            `${seg.toUpperCase()} Annual Enriched`,
            before,
            annualEnriched,
            fmt
          )
        );
      }
    });

    setVol(nextVol);
    setCosts(nextCosts);
    setTeam(nextTeam);
    setWeights(nextWeights);
    setRates(nextRates);
    setRfp(nextRfp);
    if (seedUpdate) setPmixSeed((s) => seedUpdate(s));

    const filtered = changes.filter(Boolean);
    setImportSummary(filtered);
    setToast({
      title: "CSV imported",
      items: filtered.length ? filtered.slice(0, 8) : ["No changes detected"],
    });
    setTimeout(() => setToast(null), 4500);
  }

  /* ---------- Program Mix (used inside Budget tab) ---------- */
  function ProgramMix({
    rates = {},
    weights = { Q1: 40, Q2: 30, Q3: 15, Q4: 15 },
    seed,
    onRatesChange = (fn) => fn,
  }) {
    // BRAND
    const [brandRows, setBrandRows] = React.useState([
      {
        on: true,
        label: "Display Advertising (impressions)",
        qty: seed?.displayImpr ?? 2666667,
        unit: rates.brandCPM,
        notes: "CPM ($/1k) · cost = (impr/1k) × CPM",
        family: "Brand",
        kind: "cpm",
      },
      {
        on: true,
        label: "Custom Content Assets",
        qty: 4,
        unit: rates.customContentAsset,
        notes: "per content asset",
        family: "Brand",
      },
    ]);

    // DEMAND
    const [demandRows, setDemandRows] = React.useState([
      {
        on: true,
        label: "Content Syndication Leads (per Q)",
        qty: seed?.syndLeadsPerQ ?? 375,
        unit: rates.demandCPL,
        notes: "CPL · outputs: leads",
        family: "Demand Creation",
      },
      {
        on: false,
        label: "Website Visitors (flat $/quarter)",
        qty: 4,
        unit: rates.websiteFlatQuarter,
        notes: "enter # of quarters (4 = full year)",
        family: "Demand Creation",
      },
    ]);

    // SALES ENABLEMENT
    const [enableRows, setEnableRows] = React.useState([
      {
        on: true,
        label: "1:1 Account Plans (with content)",
        qty: seed?.oneToOnePlans ?? 300,
        unit: rates.enable1to1Unit,
        notes: "per plan incl. content",
        family: "Sales Enablement",
        tiered: true,
      },
      {
        on: true,
        label: "1:Few Account Plans (with content)",
        qty: seed?.oneToFewPlans ?? 128,
        unit: rates.enable1fewUnit,
        notes: "per plan incl. content",
        family: "Sales Enablement",
        tiered: true,
        oneFew: true, // <-- identifies the base 1:Few plan row
      },
      {
        on: true,
        label: "Show Plans (with content)",
        qty: seed?.showPlans ?? 0,
        unit: rates.showPlanUnit,
        notes: "per event/show incl. content",
        family: "Sales Enablement",
        tiered: true,
        show: true,
      },
      {
        on: true,
        label: "Messaging Sequences (1:1)",
        qty: 600,
        unit: rates.seq1to1PerAcctMo,
        notes: "$/account/month · annualized (avg active ≈ 1:1 per Q × 3)",
        family: "Sales Enablement",
        msg: true,
      },
      {
        on: true,
        label: "Messaging Sequences (1:Few)",
        qty: 24, // months
        unit: rates.seq1fewPctPerMo, // 0.10 = 10%
        notes: "% of 1:Few plan per month · annualized",
        family: "Sales Enablement",
        msg: true,
        fewMsg: true, // <-- identifies the special messaging row
      },
    ]);

    // MARKET INTELLIGENCE
    const [intelRows, setIntelRows] = React.useState([
      {
        on: true,
        label: "Enriched Contacts",
        qty: 25000,
        unit: rates.enrichmentPerContact,
        notes: "per enriched contact",
        family: "Market Intelligence",
      },
      {
        on: true,
        label: "Market-Intel Signals",
        qty: 20000,
        unit: rates.marketIntelPerSignal,
        notes: "per market-intel signal",
        family: "Market Intelligence",
        tiered: true,
      },
    ]);

    // ---------- 1:Few Price Link & Tier Logic ----------
    const [linkOneFewPrice, setLinkOneFewPrice] = React.useState(true);

    // Auto-link 1:Few price to 10× 1:1 unless unchecked
    React.useEffect(() => {
      if (!linkOneFewPrice) return;
      setEnableRows((rows) =>
        rows.map((r) =>
          r.oneFew ? { ...r, unit: Number(rates.enable1to1Unit || 0) * 10 } : r
        )
      );
    }, [linkOneFewPrice, rates.enable1to1Unit]);

    // Dollar-based tiering (5% per $20k, capped 30%)
    function tieredLine(
      qty,
      unit,
      tierStep = 20000,
      stepDisc = 0.05,
      cap = 0.3
    ) {
      const q = Number(qty || 0);
      const u = Number(unit || 0);
      const gross = q * u;
      const steps = Math.floor(gross / tierStep);
      const disc = Math.min(steps * stepDisc, cap);
      return { effUnit: u * (1 - disc), cost: gross * (1 - disc), disc };
    }

    // ---- SINGLE SOURCE OF TRUTH: lineCost ----
    function lineCost(row) {
      if (!row.on) return 0;

      const qty = Number(row.qty || 0);
      const unit = Number(row.unit || 0);

      // CPM lines
      if (row.kind === "cpm") return (qty / 1000) * unit;

      // Special: Messaging Sequences (1:Few)
      // charge = (tiered cost of "1:Few Account Plans") * (pct per month) * (months)
      if (row.msg && (row.fewMsg || /\(1:Few\)/.test(row.label || ""))) {
        const oneFewPlan = enableRows.find(
          (r) => r.on && r.oneFew && /1:Few Account Plans/.test(r.label || "")
        );

        const baseCost = oneFewPlan
          ? oneFewPlan.tiered
            ? tieredLine(
                Number(oneFewPlan.qty || 0),
                Number(oneFewPlan.unit || 0)
              ).cost
            : Number(oneFewPlan.qty || 0) * Number(oneFewPlan.unit || 0)
          : 0;

        const months = qty || 12; // using qty as months
        const pct = unit; // decimal (e.g., 0.10 for 10%)
        return baseCost * pct * months;
      }

      // Tiered or normal
      return row.tiered ? tieredLine(qty, unit).cost : qty * unit;
    }

    const familySubtotal = (rows) =>
      rows.reduce((s, r) => s + Number(lineCost(r) || 0), 0);

    const subtotal = {
      brand: familySubtotal(brandRows),
      demand: familySubtotal(demandRows),
      enable: familySubtotal(enableRows),
      intel: familySubtotal(intelRows),
    };
    const grand =
      subtotal.brand + subtotal.demand + subtotal.enable + subtotal.intel;

    // ----- Charts & quick stats -----
    const donutData = [
      { name: "Brand", value: subtotal.brand, color: "#22c55e" },
      { name: "Demand Creation", value: subtotal.demand, color: "#3b82f6" },
      { name: "Sales Enablement", value: subtotal.enable, color: "#f59e0b" },
      { name: "Market Intelligence", value: subtotal.intel, color: "#8b5cf6" },
    ];

    const brandDisplayIdx = brandRows.findIndex((r) => r.kind === "cpm");
    const brandImpr =
      brandDisplayIdx >= 0 && brandRows[brandDisplayIdx].on
        ? brandRows[brandDisplayIdx].qty
        : 0;

    const demandLeads =
      demandRows.find((r) => r.on && r.label.startsWith("Content Syndication"))
        ?.qty || 0;

    const enablePlans = enableRows
      .filter(
        (r) =>
          r.on &&
          (r.oneFew || r.show || r.label.includes("1:1 Account")) &&
          !r.msg
      )
      .reduce((s, r) => s + Number(r.qty || 0), 0);

    const intelUnits = intelRows
      .filter((r) => r.on)
      .reduce((s, r) => s + Number(r.qty || 0), 0);

    const outputsBar = [
      {
        family: "Brand",
        value: brandImpr / 10000,
        raw: brandImpr,
        unit: "Impressions (÷10k)",
      },
      {
        family: "Demand Creation",
        value: demandLeads,
        raw: demandLeads,
        unit: "Leads",
      },
      {
        family: "Sales Enablement",
        value: enablePlans,
        raw: enablePlans,
        unit: "Plans",
      },
      {
        family: "Market Intelligence",
        value: intelUnits,
        raw: intelUnits,
        unit: "Contacts / Signals",
      },
    ];

    const totalW = weights.Q1 + weights.Q2 + weights.Q3 + weights.Q4 || 100;
    const byQ = [
      { q: "Q1", budget: grand * (weights.Q1 / totalW) },
      { q: "Q2", budget: grand * (weights.Q2 / totalW) },
      { q: "Q3", budget: grand * (weights.Q3 / totalW) },
      { q: "Q4", budget: grand * (weights.Q4 / totalW) },
    ];

    // ------- Pretty inputs -------
    const Labeled = ({ label, hint, unit, value, step = "0.01", onChange }) => (
      <div className="fw-field">
        <div className="fw-label">
          {label} {hint && <span className="fw-hint">{hint}</span>}
        </div>
        <div className="fw-input-wrap">
          <input
            type="number"
            step={step}
            value={value}
            onChange={(e) => onChange(Number(e.target.value || 0))}
            className="fw-input"
          />
          {unit && <div className="fw-unit">{unit}</div>}
        </div>
      </div>
    );

    const Row = ({ row, onChange }) => {
      const update = (k, v) => onChange({ ...row, [k]: v });
      // compute detail (effective unit & discount) for tiered lines, otherwise show computed cost
      const detail = row.tiered
        ? tieredLine(Number(row.qty || 0), Number(row.unit || 0))
        : { effUnit: row.unit, cost: lineCost(row), disc: 0 };

      return (
        <div className="row">
          <input
            type="checkbox"
            checked={row.on}
            onChange={(e) => update("on", e.target.checked)}
          />
          <div>
            <div>{row.label}</div>
            <div className="sub">{row.notes}</div>
          </div>
          {/* Qty */}
          <NumberField
            value={row.qty}
            maxFraction={0}
            onChange={(n) => update("qty", n)}
          />
          {/* Unit */}
          <NumberField
            value={row.unit}
            maxFraction={4}
            onChange={(n) => update("unit", n)}
          />
          <div className="sub">
            {row.kind === "cpm"
              ? "CPM"
              : row.msg && (row.fewMsg || /\(1:Few\)/.test(row.label || ""))
              ? `≈ ${fmt(row.unit * 100, 0)}% of 1:Few plan/mo × ${
                  row.qty || 12
                } mo`
              : row.tiered
              ? `tiered ≈ $${Number(detail.effUnit).toFixed(
                  2
                )}/unit (disc ${Math.round(detail.disc * 100)}%)`
              : "\u00A0"}
          </div>
          <div className="money">{money(detail.cost)}</div>
        </div>
      );
    };

    const Fam = ({ title, rows, setRows, subtotal, tone = "brand" }) => (
      <div className={`family tone-${tone}`}>
        <h4>
          {title} <span className="sub">Subtotal: {money(subtotal)}</span>
        </h4>
        <div className="row" style={{ fontWeight: 700 }}>
          <div></div>
          <div>Line Item</div>
          <div>Qty</div>
          <div>Unit Cost</div>
          <div>Notes</div>
          <div className="money">Line Cost</div>
        </div>
        {rows.map((r, i) => (
          <Row
            key={i}
            row={r}
            onChange={(nr) => {
              const cp = [...rows];
              cp[i] = nr;
              setRows(cp);
            }}
          />
        ))}
      </div>
    );

    // Annual Display Budget control — two-way with the CPM line
    const displayBudget = Math.round(
      (brandImpr / 1000) * (rates.brandCPM || 0)
    );
    const applyDisplayBudget = (val) => {
      const qty = Math.max(
        0,
        Math.round(((val || 0) / (rates.brandCPM || 1)) * 1000)
      );
      setBrandRows((rows) => {
        const cp = [...rows];
        if (brandDisplayIdx >= 0)
          cp[brandDisplayIdx] = { ...cp[brandDisplayIdx], qty };
        return cp;
      });
    };

    return (
      <div>
        <div className="pmix">
          {/* Left Executive Sidebar */}
          <div className="panel">
            <h2>Program Mix Planner</h2>
            <div className="small">
              Powered by Assumptions. Fine-tune line items below.
            </div>

            {/* Totals */}
            <div className="group">
              <h3>Selected Budget</h3>
              <div
                className="row-grid"
                style={{ gridTemplateColumns: "1fr 180px" }}
              >
                <div className="lbl">Estimated Quarterly Budget</div>
                <input type="text" readOnly value={money(grand / 4)} />
                <div className="lbl">Estimated Annual Budget</div>
                <input type="text" readOnly value={money(grand)} />
              </div>
            </div>

            {/* Link control */}
            <div className="group">
              <h3>Price Link</h3>
              <div className="small">
                If enabled, the 1:Few plan price always equals <b>10×</b> the
                1:1 plan price.
              </div>
              <div className="input-row">
                <label>Link 1:Few = 10× 1:1</label>
                <input
                  type="checkbox"
                  checked={linkOneFewPrice}
                  onChange={(e) => setLinkOneFewPrice(e.target.checked)}
                />
                <span className="small">
                  {linkOneFewPrice
                    ? "Linked 10× to 1:1 (uncheck to edit)"
                    : "Unlinked / editable"}
                </span>
              </div>
            </div>

            {/* Brand & Content */}
            <div className="group">
              <h3>Brand & Content</h3>
              <div className="row-grid">
                <Labeled
                  label="Brand CPM"
                  hint="$ per 1,000 impressions"
                  unit="$ / 1k"
                  value={rates.brandCPM}
                  step="0.01"
                  onChange={(v) =>
                    onRatesChange((s) => ({ ...s, brandCPM: v }))
                  }
                />
                <Labeled
                  label="Custom Content Asset"
                  hint="per asset"
                  unit="$ / asset"
                  value={rates.customContentAsset}
                  step="1"
                  onChange={(v) =>
                    onRatesChange((s) => ({ ...s, customContentAsset: v }))
                  }
                />
                <Labeled
                  label="Annual Display Budget"
                  hint="auto → impressions"
                  unit="$ / year"
                  value={displayBudget}
                  step="1"
                  onChange={(v) => applyDisplayBudget(v)}
                />
              </div>
            </div>

            {/* Demand */}
            <div className="group">
              <h3>Demand</h3>
              <div className="row-grid">
                <Labeled
                  label="Syndication CPL"
                  unit="$ / lead"
                  value={rates.demandCPL}
                  onChange={(v) =>
                    onRatesChange((s) => ({ ...s, demandCPL: v }))
                  }
                />
                <Labeled
                  label="Website Visitors (flat $/quarter)"
                  unit="$ / quarter"
                  value={rates.websiteFlatQuarter}
                  onChange={(v) =>
                    onRatesChange((s) => ({ ...s, websiteFlatQuarter: v }))
                  }
                />
              </div>
            </div>

            {/* Enablement Plans */}
            <div className="group">
              <h3>Sales Enablement — Plans (incl. content)</h3>
              <div className="row-grid">
                <Labeled
                  label="1:1 Plan"
                  unit="$ / plan"
                  value={rates.enable1to1Unit}
                  step="1"
                  onChange={(v) =>
                    onRatesChange((s) => ({ ...s, enable1to1Unit: v }))
                  }
                />
                <Labeled
                  label="1:Few Plan"
                  unit="$ / plan"
                  value={
                    linkOneFewPrice
                      ? rates.enable1to1Unit * 10
                      : rates.enable1fewUnit
                  }
                  step="1"
                  onChange={(v) =>
                    onRatesChange((s) => ({ ...s, enable1fewUnit: v }))
                  }
                />
                <Labeled
                  label="Show Plan"
                  hint="events/conferences"
                  unit="$ / show"
                  value={rates.showPlanUnit ?? 100}
                  step="1"
                  onChange={(v) =>
                    onRatesChange((s) => ({ ...s, showPlanUnit: v }))
                  }
                />
              </div>
            </div>

            {/* Messaging */}
            <div className="group">
              <h3>Messaging Sequences</h3>
              <div className="row-grid">
                <Labeled
                  label="1:1 Sequences"
                  unit="$ / acct / month"
                  value={rates.seq1to1PerAcctMo}
                  step="0.01"
                  onChange={(v) =>
                    onRatesChange((s) => ({ ...s, seq1to1PerAcctMo: v }))
                  }
                />
                <Labeled
                  label="1:Few Sequences"
                  unit="% of 1:Few plan / month"
                  value={rates.seq1fewPctPerMo}
                  step="0.01"
                  onChange={(v) =>
                    onRatesChange((s) => ({ ...s, seq1fewPctPerMo: v }))
                  }
                />
              </div>
            </div>

            {/* Data & Intelligence */}
            <div className="group">
              <h3>Data & Market Intelligence</h3>
              <div className="row-grid">
                <Labeled
                  label="Enrichment"
                  unit="$ / contact"
                  value={rates.enrichmentPerContact}
                  step="0.01"
                  onChange={(v) =>
                    onRatesChange((s) => ({ ...s, enrichmentPerContact: v }))
                  }
                />
                <Labeled
                  label="Market-Intel"
                  unit="$ / signal"
                  value={rates.marketIntelPerSignal}
                  step="0.01"
                  onChange={(v) =>
                    onRatesChange((s) => ({ ...s, marketIntelPerSignal: v }))
                  }
                />
              </div>
              <div className="small">
                <b>Tiered pricing:</b> 5% off each $20k line-item, cap 30%
                (applies to Account/Show Plans & Intel Signals).
              </div>
            </div>
          </div>

          {/* Right: families + totals */}
          <div>
            <div className="family">
              <h4>Quick Actions</h4>
              <div className="sub">Preload or template your inputs.</div>
              <div className="uploader">
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => handleCsvUpload(e.target.files?.[0])}
                />
                <button className="btn secondary" onClick={downloadCSVTemplate}>
                  Download CSV template
                </button>
              </div>
            </div>

            <Fam
              title="Brand"
              rows={brandRows}
              setRows={setBrandRows}
              subtotal={subtotal.brand}
              tone="brand"
            />
            <Fam
              title="Demand Creation"
              rows={demandRows}
              setRows={setDemandRows}
              subtotal={subtotal.demand}
              tone="demand"
            />
            <Fam
              title="Sales Enablement"
              rows={enableRows}
              setRows={setEnableRows}
              subtotal={subtotal.enable}
              tone="enable"
            />
            <Fam
              title="Market Intelligence"
              rows={intelRows}
              setRows={setIntelRows}
              subtotal={subtotal.intel}
              tone="intel"
            />

            <div className="subtotal" style={{ fontSize: "1.05rem" }}>
              Grand Total (selected): {money(grand)}
            </div>
          </div>
        </div>

        {/* Mix by Family */}
        <div className="table-block" style={{ marginTop: 14 }}>
          <div className="header-row">
            <div className="section-title">Mix by Family (share of spend)</div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={donutData}
                dataKey="value"
                nameKey="name"
                innerRadius={48}
                outerRadius={78}
                paddingAngle={3}
              >
                {donutData.map((d, i) => (
                  <Cell key={i} fill={d.color} />
                ))}
              </Pie>
              <Legend />
              <RTooltip formatter={(v, n) => [money(v), n]} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Budget by Family */}
        <div className="table-block">
          <div className="header-row">
            <div className="section-title">Budget by Family (spend)</div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart
              data={[
                { family: "Brand", budget: subtotal.brand },
                { family: "Demand Creation", budget: subtotal.demand },
                { family: "Sales Enablement", budget: subtotal.enable },
                { family: "Market Intelligence", budget: subtotal.intel },
              ]}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="family" />
              <YAxis />
              <RTooltip formatter={(v) => [money(v), "Spend"]} />
              <Bar dataKey="budget">
                <Cell fill="#22c55e" />
                <Cell fill="#3b82f6" />
                <Cell fill="#f59e0b" />
                <Cell fill="#8b5cf6" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Outputs */}
        <div className="table-block">
          <div className="header-row">
            <div className="section-title">Outputs (units)</div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={outputsBar}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="family" />
              <YAxis />
              <RTooltip
                formatter={(v, _, p) => [
                  `${p.payload.raw.toLocaleString()} ${p.payload.unit}`,
                  p.payload.family,
                ]}
              />
              <Bar dataKey="value">
                {outputsBar.map((d, i) => (
                  <Cell
                    key={i}
                    fill={
                      d.family === "Brand"
                        ? "#22c55e"
                        : d.family === "Demand Creation"
                        ? "#3b82f6"
                        : d.family === "Sales Enablement"
                        ? "#f59e0b"
                        : "#8b5cf6"
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <small className="badge">
            Brand shows impressions ×10k for readability.
          </small>
        </div>

        {/* Budget by Quarter */}
        <div className="table-block">
          <div className="header-row">
            <div className="section-title">Budget by Quarter</div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={byQ}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="q" />
              <YAxis />
              <RTooltip formatter={(v) => [money(v), "Budget"]} />
              <Line
                type="monotone"
                dataKey="budget"
                stroke="#0f172a"
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  /* ===================== FORECAST TAB ===================== */
  function TabForecast() {
    const sections = [
      {
        key: "retail",
        title: "Retail",
        strategy:
          "Strategy: 1:1 + 1:Few ABM. Sales led with marketing support.",
        f: model.retailF,
      },
      {
        key: "ad",
        title: "AD Show & Account-Based Outbound",
        strategy:
          "Strategy: 1:1 + 1:Few ABM. Sales led with marketing support.",
        f: model.adF,
      },
      {
        key: "ae",
        title: "AE SDR Show & Account-Based Outbound",
        strategy:
          "Strategy: 1:Few ABM. Sales and marketing in full collaboration.",
        f: model.aeF,
      },
    ];

    const METRICS = (f) => [
      ["Pixel Leads (Prospects/MQL Signal)", f.v * applied.r.pixelToMQL, false],
      ["MQLs (Inbound, Paid)", f.mql, false],
      ["Meetings Scheduled (SQL)", f.sql, false],
      ["Meetings Completed", f.meetings, false],
      ["Qualified Deals", f.qualified, false],
      ["Closed/Won Deals", f.won, false],
      ["Unweighted Pipeline", f.pipeline, true],
      ["Revenue", f.revenue, true],
    ];

    return (
      <>
        <h1>STAR 2026 GTM Math – 12 Month Forecast</h1>

        <div className="table-block" style={{ marginBottom: 12 }}>
          <div className="header-row">
            <div className="section-title">Preload from CSV</div>
            <div className="small">
              Use the template to update plays, visitors, FTEs, and quarterly
              weights.
            </div>
          </div>
          <div
            className="uploader"
            style={{ display: "flex", gap: 10, alignItems: "center" }}
          >
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => handleCsvUpload(e.target.files?.[0])}
            />
            <button className="btn secondary" onClick={downloadCSVTemplate}>
              Download CSV template
            </button>
          </div>
        </div>

        {sections.map((sec) => (
          <div className="table-block" key={sec.key}>
            <div className="header-row">
              <div className="section-title">{sec.title}</div>
              <div className="small">{sec.strategy}</div>
            </div>
            <table className="forecast-table">
              <thead>
                <tr>
                  <th></th>
                  {months.map((m, j) => (
                    <th key={j}>{m}</th>
                  ))}
                  <th className="totals-header">2026 Totals</th>
                </tr>
              </thead>
              <tbody>
                <tr className="highlight">
                  <td>Est. Monthly STAR Site Visitors</td>
                  {months.map((_, j) => (
                    <td key={j}>{fmt(sec.f.v, 0)}</td>
                  ))}
                  <td>{fmt(sec.f.v * 12, 0)}</td>
                </tr>
                {METRICS(sec.f).map(([label, val, currency], idx) => (
                  <tr key={idx}>
                    <td>{label}</td>
                    {months.map((_, j) => (
                      <td key={j}>
                        {currency ? money(val / 12) : fmt(val / 12)}
                      </td>
                    ))}
                    <td>{currency ? money(val) : fmt(val)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </>
    );
  }

  /* ===================== BUDGET TAB (Program Mix + Narrative) ===================== */

  const getPlanningBenchmarks = (rates) => [
    [
      "Monthly Web Visitors-to-MQL Conversion Rate",
      `${fmt(rates.visitorToMQL * 100, 2)}%`,
      "B2B Industry Benchmark",
    ],
    [
      "Pixel Lead to MQL Conversion Rate",
      `${fmt(rates.pixelToMQL * 100, 2)}%`,
      "Fairway Data",
    ],
    [
      "MQL-to-SQL (Meeting Scheduled) Conversion",
      `${fmt(rates.mqlToSQL * 100, 2)}%`,
      "B2B Industry Benchmark",
    ],
    [
      "SQL-to-Meeting Completed Rate",
      `${fmt(rates.sqlToMeeting * 100, 2)}%`,
      "B2B Industry Benchmark",
    ],
    [
      "Meeting Completed-to-Qualified Deal Rate",
      `${fmt(rates.meetingToQualified * 100, 2)}%`,
      "B2B Industry Benchmark",
    ],
    [
      "Qualified-to-Closed/Won Deal Rate",
      `${fmt(rates.qualifiedToWon * 100, 2)}%`,
      "B2B Industry Benchmark",
    ],
    [
      "STAR Avg. Deal Size (New Logo)",
      money(rates.avgDeal),
      "2025 STAR Average Deal Size (New Logo)",
    ],
  ];
  const PLANNING_BENCHMARKS = getPlanningBenchmarks(applied.r);

  function moneyQ(q) {
    return { ...q, annual: q.Q126 + q.Q226 + q.Q326 + q.Q426 };
  }
  function freezePerQuarterCount(perQ) {
    return { Q126: perQ, Q226: perQ, Q326: perQ, Q426: perQ, annual: perQ * 4 };
  }
  function splitByWeights(annualUnits) {
    const totalW = weights.Q1 + weights.Q2 + weights.Q3 + weights.Q4 || 100;
    return {
      Q126: annualUnits * (weights.Q1 / totalW),
      Q226: annualUnits * (weights.Q2 / totalW),
      Q326: annualUnits * (weights.Q3 / totalW),
      Q426: annualUnits * (weights.Q4 / totalW),
      annual: annualUnits,
    };
  }
  function costFixedUnit(qshape, unit) {
    return {
      Q126: qshape.Q126 * unit,
      Q226: qshape.Q226 * unit,
      Q326: qshape.Q326 * unit,
      Q426: qshape.Q426 * unit,
      annual: qshape.annual * unit,
    };
  }

  function BudgetBlock({ header, budgetHeader, rows }) {
    return (
      <div className="table-block purple">
        <div className="purple-band">
          <div className="section-title white">{header}</div>
        </div>
        <table className="planning-table">
          <thead>
            <tr>
              <th className="w-18"> </th>
              <th className="w-18">Planning Numbers</th>
              <th className="w-16">Budget Category</th>
              <th>Description</th>
              <th className="w-16">{budgetHeader}</th>
              <th className="w-8 center">Q126</th>
              <th className="w-8 center">Q226</th>
              <th className="w-8 center">Q326</th>
              <th className="w-8 center">Q426</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td className="label-cell">{r.label}</td>
                <td className="prewrap">{r.plan}</td>
                <td>{r.cat}</td>
                <td className="prewrap">{r.desc}</td>
                <td>{r.total != null ? money(r.total) : ""}</td>
                <td className="center">{r.Q && money(r.Q.Q126)}</td>
                <td className="center">{r.Q && money(r.Q.Q226)}</td>
                <td className="center">{r.Q && money(r.Q.Q326)}</td>
                <td className="center">{r.Q && money(r.Q.Q426)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function TabBudget() {
    const annualFromPerMonthPerQ = (perMonth, perQ) => perMonth * 3 * perQ;

    // Retail
    const r1to1Q = freezePerQuarterCount(rfp.retail.oneToOnePerQ);
    const r1fewQ = freezePerQuarterCount(rfp.retail.oneToFewPerQ);
    const rLeadsQ = freezePerQuarterCount(
      rfp.retail.oneToOnePerQ * rfp.retail.leadsPerOneToOne
    );
    const rEnriched = splitByWeights(rfp.retail.annualEnriched);
    const rEnrich1to1Q = moneyQ({
      Q126: rEnriched.Q126 * 0.25,
      Q226: rEnriched.Q226 * 0.25,
      Q326: rEnriched.Q326 * 0.25,
      Q426: rEnriched.Q426 * 0.25,
    });
    const rEnrich1fewQ = moneyQ({
      Q126: rEnriched.Q126 * 0.75,
      Q226: rEnriched.Q226 * 0.75,
      Q326: rEnriched.Q326 * 0.75,
      Q426: rEnriched.Q426 * 0.75,
    });

    const rCostEnable1to1Q = costFixedUnit(r1to1Q, applied.r.enable1to1Unit);
    const rCostEnable1fewQ = costFixedUnit(r1fewQ, applied.r.enable1fewUnit);
    const rCostLeadsQ = costFixedUnit(rLeadsQ, applied.r.demandCPL);
    const rCostEnrich1to1Q = costFixedUnit(
      rEnrich1to1Q,
      applied.r.enrichmentPerContact
    );
    const rCostEnrich1fewQ = costFixedUnit(
      rEnrich1fewQ,
      applied.r.enrichmentPerContact
    );

    const rSeq1to1Annual = annualFromPerMonthPerQ(
      applied.r.seq1to1PerAcctMo,
      rfp.retail.oneToOnePerQ
    );
    const rSeq1fewAnnual =
      4 *
      rfp.retail.oneToFewPerQ *
      (applied.r.enable1fewUnit * applied.r.seq1fewPctPerMo);
    const rSeqQ = frontLoadAnnualToQuarters(rSeq1to1Annual + rSeq1fewAnnual);

    // AD
    const ad1to1Q = freezePerQuarterCount(rfp.ad.oneToOnePerQ);
    const ad1fewQ = freezePerQuarterCount(rfp.ad.oneToFewPerQ);
    const adLeadsQ = freezePerQuarterCount(rfp.ad.syndLeadsPerQ);
    const adEnrichedQ = splitByWeights(rfp.ad.annualEnriched);

    const adCostEnable1to1Q = costFixedUnit(ad1to1Q, applied.r.enable1to1Unit);
    const adCostEnable1fewQ = costFixedUnit(ad1fewQ, applied.r.enable1fewUnit);
    const adCostLeadsQ = costFixedUnit(adLeadsQ, applied.r.demandCPL);
    const adCostEnrichQ = costFixedUnit(
      adEnrichedQ,
      applied.r.enrichmentPerContact
    );

    const adSeq1to1Annual = annualFromPerMonthPerQ(
      applied.r.seq1to1PerAcctMo,
      rfp.ad.oneToOnePerQ
    );
    const adSeq1fewAnnual =
      4 *
      rfp.ad.oneToFewPerQ *
      (applied.r.enable1fewUnit * applied.r.seq1fewPctPerMo);
    const adSeqQ = frontLoadAnnualToQuarters(adSeq1to1Annual + adSeq1fewAnnual);

    // AE
    const ae1fewQ = freezePerQuarterCount(rfp.ae.oneToFewPerQ);
    const aeShowQ = freezePerQuarterCount(rfp.ae.showPlansPerQ);
    const aeEnrichedQ = splitByWeights(rfp.ae.annualEnriched);

    const aeCostEnable1fewQ = costFixedUnit(ae1fewQ, applied.r.enable1fewUnit);
    const aeCostShowsQ = costFixedUnit(aeShowQ, applied.r.showPlanUnit);
    const aeCostEnrichQ = costFixedUnit(
      aeEnrichedQ,
      applied.r.enrichmentPerContact
    );

    const aeSeq1fewAnnual =
      4 *
      rfp.ae.oneToFewPerQ *
      (applied.r.enable1fewUnit * applied.r.seq1fewPctPerMo);
    const aeSeqQ = frontLoadAnnualToQuarters(aeSeq1fewAnnual);

    // Featherlite
    const fe1fewQ = freezePerQuarterCount(rfp.feather.oneToFewPerQ);
    const feShowQ = freezePerQuarterCount(rfp.feather.showPlansPerQ);
    const feEnrichedQ = splitByWeights(rfp.feather.annualEnriched);

    const feCostEnable1fewQ = costFixedUnit(fe1fewQ, applied.r.enable1fewUnit);
    const feCostShowsQ = costFixedUnit(feShowQ, applied.r.showPlanUnit);
    const feCostEnrichQ = costFixedUnit(
      feEnrichedQ,
      applied.r.enrichmentPerContact
    );

    const feSeq1fewAnnual =
      4 *
      rfp.feather.oneToFewPerQ *
      (applied.r.enable1fewUnit * applied.r.seq1fewPctPerMo);
    const feSeqQ = frontLoadAnnualToQuarters(feSeq1fewAnnual);

    const toRow = (label, plan, cat, desc, qshape) => ({
      label,
      plan,
      cat,
      desc,
      total: qshape ? qshape.annual : null,
      Q: qshape,
    });

    const retailRows = [
      toRow(
        "Content Syndication (for 1:1)",
        `Leads/Q = ${rfp.retail.oneToOnePerQ} × ${rfp.retail.leadsPerOneToOne}`,
        "Demand Gen – 3rd Party",
        "Account-focused content syndication for 1:1 accounts.",
        rCostLeadsQ
      ),
      toRow(
        "1:1 Account Plans (with content)",
        `${rfp.retail.oneToOnePerQ} per Q × ${money(applied.r.enable1to1Unit)}`,
        "Enablement",
        "Research, plan creation, and sequence-ready content.",
        rCostEnable1to1Q
      ),
      toRow(
        "1:Few Account Plans (with content)",
        `${rfp.retail.oneToFewPerQ} per Q × ${money(applied.r.enable1fewUnit)}`,
        "Enablement",
        "Quarterly 1:Few campaign (industry/segment plan).",
        rCostEnable1fewQ
      ),
      toRow(
        "Messaging Sequences (1:1 + 1:Few)",
        `1:1 $${applied.r.seq1to1PerAcctMo}/acct/mo • 1:Few ${fmt(
          applied.r.seq1fewPctPerMo * 100,
          0
        )}% of plan/mo`,
        "Enablement",
        "Ongoing multithreaded sequences; annualized from per-month.",
        rSeqQ
      ),
      toRow(
        "Enriched Contacts (1:1 plays)",
        `25% of ${rfp.retail.annualEnriched} /yr (weighted)`,
        "Data Support",
        "Enriched contacts supporting 1:1 plays.",
        rCostEnrich1to1Q
      ),
      toRow(
        "Enriched Contacts (1:Few campaign)",
        `75% of ${rfp.retail.annualEnriched} /yr (weighted)`,
        "Data Support",
        "Enriched contacts supporting 1:Few campaigns.",
        rCostEnrich1fewQ
      ),
    ];

    const adRows = [
      toRow(
        "Content Syndication Leads",
        `${rfp.ad.syndLeadsPerQ} per Q × ${money(applied.r.demandCPL)}`,
        "Demand Gen - 3rd Party",
        "Account-focused content syndication.",
        adCostLeadsQ
      ),
      toRow(
        "1:1 Account Plans (with content)",
        `${rfp.ad.oneToOnePerQ} per Q × ${money(applied.r.enable1to1Unit)}`,
        "Enablement",
        "Per AD quarterly 1:1 plan support.",
        adCostEnable1to1Q
      ),
      toRow(
        "1:Few Account Plans (with content)",
        `${rfp.ad.oneToFewPerQ} per Q × ${money(applied.r.enable1fewUnit)}`,
        "Enablement",
        "Quarterly 1:Few plan support.",
        adCostEnable1fewQ
      ),
      toRow(
        "Messaging Sequences (1:1 + 1:Few)",
        `1:1 $${applied.r.seq1to1PerAcctMo}/acct/mo • 1:Few ${fmt(
          applied.r.seq1fewPctPerMo * 100,
          0
        )}% of plan/mo`,
        "Enablement",
        "Ongoing sequences across AD accounts/campaigns.",
        adSeqQ
      ),
      toRow(
        "Enriched Contacts (all ADs)",
        `${rfp.ad.annualEnriched} / yr (weighted)`,
        "Data Support",
        "Enriched + validated contacts to support AD programs.",
        adCostEnrichQ
      ),
    ];

    const aeRows = [
      toRow(
        "1:Few Show Plans (with content)",
        `${rfp.ae.oneToFewPerQ} per Q × ${money(applied.r.enable1fewUnit)}`,
        "Enablement",
        "Show-based outbound plans + content for AE sequences.",
        aeCostEnable1fewQ
      ),
      toRow(
        "Show Plans (events)",
        `${rfp.ae.showPlansPerQ} per Q × ${money(applied.r.showPlanUnit)}`,
        "Enablement",
        "Event-specific playbooks & content.",
        aeCostShowsQ
      ),
      toRow(
        "Messaging Sequences (1:Few)",
        `${fmt(applied.r.seq1fewPctPerMo * 100, 0)}% of plan/mo`,
        "Enablement",
        "Ongoing AE sequences for show-based programs.",
        aeSeqQ
      ),
      toRow(
        "Enriched + Validated Contacts",
        `${rfp.ae.annualEnriched} / yr (weighted)`,
        "Data Support",
        "Contacts to support AE outbound.",
        aeCostEnrichQ
      ),
    ];

    const featherRows = [
      toRow(
        "1:Few Show Plans (with content)",
        `${rfp.feather.oneToFewPerQ} per Q × ${money(
          applied.r.enable1fewUnit
        )}`,
        "Enablement",
        "Show-based 1:Few plans for Featherlite.",
        feCostEnable1fewQ
      ),
      toRow(
        "Show Plans (events)",
        `${rfp.feather.showPlansPerQ} per Q × ${money(applied.r.showPlanUnit)}`,
        "Enablement",
        "Event-specific playbooks & content.",
        feCostShowsQ
      ),
      toRow(
        "Messaging Sequences (1:Few)",
        `${fmt(applied.r.seq1fewPctPerMo * 100, 0)}% of plan/mo`,
        "Enablement",
        "Ongoing sequences for Featherlite programs.",
        feSeqQ
      ),
      toRow(
        "Enriched + Validated Contacts",
        `${rfp.feather.annualEnriched} / yr (weighted)`,
        "Data Support",
        "Contacts aligned to Featherlite accounts.",
        feCostEnrichQ
      ),
    ];

    const RfpControl = ({ title, path, fields }) => (
      <div className="card" style={{ margin: "10px 0" }}>
        <h2>{title}</h2>
        {fields.map(({ label, key, step = 1 }) => (
          <div className="input-row" key={key}>
            <label>{label}</label>
            <input
              type="number"
              step={step}
              value={rfp[path][key]}
              onChange={(e) =>
                setRfp((s) => ({
                  ...s,
                  [path]: { ...s[path], [key]: Number(e.target.value || 0) },
                }))
              }
            />
          </div>
        ))}
      </div>
    );

    return (
      <>
        <h1>STAR 2026 GTM Math – Budget & Forecast</h1>

        {/* Program Mix (interactive) */}
        <div className="table-block">
          <div className="header-row">
            <div className="section-title">
              Program Mix (Line-Items + Tiered Pricing)
            </div>
            <div className="small">
              Scenario: <b>{scenario}</b> (affects conversions & CPL; unit
              prices controlled here)
            </div>
          </div>
          <ProgramMix
            key={`pmix-${pmixSeed.version}`}
            rates={applied.r}
            weights={weights}
            seed={pmixSeed}
            onRatesChange={setRates}
          />
        </div>

        {/* Benchmarks */}
        <div className="table-block purple">
          <div className="header-row" style={{ borderBottomColor: "#5e2a8a" }}>
            <div className="section-title" style={{ color: "#5e2a8a" }}>
              2026 Planning Benchmarks
            </div>
            <div className="small">
              Scenario selected: <b>{scenario}</b>{" "}
              <span className="badge">applies to conversions & CPL</span>
            </div>
          </div>
          <table className="benchmark-table">
            <thead>
              <tr>
                <th>2026 Planning Benchmarks</th>
                <th>Planning Numbers</th>
                <th>Benchmark Source</th>
              </tr>
            </thead>
            <tbody>
              {PLANNING_BENCHMARKS.map((b, i) => (
                <tr key={i}>
                  <td>{b[0]}</td>
                  <td className="right">{b[1]}</td>
                  <td>{b[2]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* RFP Controls */}
        <div className="grid two" style={{ marginBottom: 10 }}>
          <RfpControl
            title="Retail RFP Helper"
            path="retail"
            fields={[
              { label: "1:1 accounts per quarter", key: "oneToOnePerQ" },
              {
                label: "Leads per 1:1 account (per Q)",
                key: "leadsPerOneToOne",
              },
              { label: "1:Few campaigns per quarter", key: "oneToFewPerQ" },
              { label: "Annual enriched contacts", key: "annualEnriched" },
            ]}
          />
          <div className="grid two">
            <RfpControl
              title="Account Directors (5 FTEs)"
              path="ad"
              fields={[
                { label: "1:1 plans per quarter", key: "oneToOnePerQ" },
                { label: "1:Few campaigns per quarter", key: "oneToFewPerQ" },
                {
                  label: "Syndication leads per quarter",
                  key: "syndLeadsPerQ",
                },
                { label: "Annual enriched contacts", key: "annualEnriched" },
              ]}
            />
            <RfpControl
              title="AEs / SDRs (2 FTEs) — Show Programs"
              path="ae"
              fields={[
                { label: "1:Few show plans per quarter", key: "oneToFewPerQ" },
                {
                  label: "Show plans (count) per quarter",
                  key: "showPlansPerQ",
                },
                { label: "Annual enriched contacts", key: "annualEnriched" },
              ]}
            />
            <RfpControl
              title="Featherlite ADs (5 FTEs) — Show Programs"
              path="feather"
              fields={[
                { label: "1:Few show plans per quarter", key: "oneToFewPerQ" },
                {
                  label: "Show plans (count) per quarter",
                  key: "showPlansPerQ",
                },
                { label: "Annual enriched contacts", key: "annualEnriched" },
              ]}
            />
          </div>
        </div>

        {/* Blocks */}
        <BudgetBlock
          header="STAR Retail (1 FTE)"
          budgetHeader="Est. 2026 Retail ABM Budget"
          rows={retailRows}
        />
        <BudgetBlock
          header="STAR Account Directors (5 FTEs)"
          budgetHeader="Est. 2026 AD ABM Budget"
          rows={adRows}
        />
        <BudgetBlock
          header="STAR Account Executives (2 FTEs) — Show Programs"
          budgetHeader="Est. 2026 AE ABM Budget"
          rows={aeRows}
        />
        <BudgetBlock
          header="Featherlite Account Directors (5 FTEs) — Show Programs"
          budgetHeader="Est. 2026 Featherlite AD Budget"
          rows={featherRows}
        />
      </>
    );
  }

  /* ===================== ASSUMPTIONS TAB ===================== */
  function Assumptions() {
    const setRate = (k, v) => setRates((s) => ({ ...s, [k]: Number(v) }));
    const setVolK = (k, v) => setVol((s) => ({ ...s, [k]: Number(v) }));
    const setCost = (k, v) => setCosts((s) => ({ ...s, [k]: Number(v) }));
    const setTeamK = (k, v) => setTeam((s) => ({ ...s, [k]: Number(v) }));
    const setW = (k, v) => setWeights((s) => ({ ...s, [k]: Number(v) }));
    const wSum = weights.Q1 + weights.Q2 + weights.Q3 + weights.Q4;

    return (
      <>
        <h1>Assumptions & Scenarios</h1>

        {/* CSV preload */}
        <div className="table-block" style={{ marginBottom: 12 }}>
          <div className="header-row">
            <div className="section-title">Preload from CSV</div>
            <div className="small">
              Maps rows → plays, syndication, visitors, weights, FTEs, and
              optional “rates”.
            </div>
          </div>
          <div className="uploader">
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => handleCsvUpload(e.target.files?.[0])}
            />
            <button
              className="btn secondary"
              onClick={() => setImportSummary([])}
            >
              Clear Summary
            </button>
            <button className="btn secondary" onClick={downloadCSVTemplate}>
              Download CSV template
            </button>
          </div>
          {importSummary.length > 0 && (
            <div className="card" style={{ marginTop: 10 }}>
              <h2>Import Summary</h2>
              <ul style={{ marginTop: 6 }}>
                {importSummary.map((line, i) => (
                  <li key={i} className="small">
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="grid three">
          <div className="card">
            <h2>Scenario</h2>
            <div className="input-row">
              <label>Preset</label>
              <select
                value={scenario}
                onChange={(e) => setScenario(e.target.value)}
              >
                {Object.keys(SCENARIOS).map((k) => (
                  <option key={k}>{k}</option>
                ))}
              </select>
              <span className="small">
                Conv × {SCENARIOS[scenario].convMult}, CPL ×{" "}
                {SCENARIOS[scenario].cplMult}
              </span>
            </div>
            <div className="small">
              Changes update Budget & Forecast tabs immediately.
            </div>
          </div>

          <div className="card">
            <h2>Brand Budget Allocation by Quarter</h2>
            {["Q1", "Q2", "Q3", "Q4"].map((q) => (
              <div className="input-row" key={q}>
                <label>{q} (%)</label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={weights[q]}
                  onChange={(e) => setW(q, Number(e.target.value))}
                />
                <input
                  type="number"
                  value={weights[q]}
                  onChange={(e) => setW(q, Number(e.target.value))}
                />
              </div>
            ))}
            <div className="small">
              Must sum to 100. Current = <b>{wSum}%</b>
            </div>
          </div>

          <div className="card">
            <h2>Team Footprint</h2>
            <div className="input-row">
              <label>Retail Reps</label>
              <input
                type="number"
                value={team.retailReps}
                onChange={(e) => setTeamK("retailReps", e.target.value)}
              />
            </div>
            <div className="input-row">
              <label>Account Directors</label>
              <input
                type="number"
                value={team.adReps}
                onChange={(e) => setTeamK("adReps", e.target.value)}
              />
            </div>
            <div className="input-row">
              <label>AE / SDRs</label>
              <input
                type="number"
                value={team.aeReps}
                onChange={(e) => setTeamK("aeReps", e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="grid two" style={{ marginTop: 12 }}>
          <div className="card">
            <h2>Conversion Rates & Value</h2>
            {[
              ["Visitor → MQL", "visitorToMQL", 2],
              ["Pixel Lead → MQL", "pixelToMQL", 2],
              ["MQL → SQL (Meeting Scheduled)", "mqlToSQL", 2],
              ["SQL → Meeting Completed", "sqlToMeeting", 2],
              ["Meeting → Qualified", "meetingToQualified", 2],
              ["Qualified → Won", "qualifiedToWon", 2],
            ].map(([label, key, p]) => (
              <div className="input-row" key={key}>
                <label>{label}</label>
                <input
                  type="number"
                  step="0.001"
                  value={rates[key]}
                  onChange={(e) => setRate(key, e.target.value)}
                />
                <span className="small">{fmt(rates[key] * 100, p)}%</span>
              </div>
            ))}
            <div className="input-row">
              <label>Avg Deal Size (New Logo)</label>
              <input
                type="number"
                value={rates.avgDeal}
                onChange={(e) => setRate("avgDeal", e.target.value)}
              />
              <span className="small">{money(rates.avgDeal)}</span>
            </div>
          </div>

          <div className="card">
            <h2>Traffic / Volume Inputs</h2>
            <div className="input-row">
              <label>Retail Visitors / Month</label>
              <input
                type="number"
                value={vol.retailVisitorsMo}
                onChange={(e) => setVolK("retailVisitorsMo", e.target.value)}
              />
            </div>
            <div className="input-row">
              <label>AD Visitors / Month</label>
              <input
                type="number"
                value={vol.adVisitorsMo}
                onChange={(e) => setVolK("adVisitorsMo", e.target.value)}
              />
            </div>
            <div className="input-row">
              <label>AE Visitors / Month</label>
              <input
                type="number"
                value={vol.aeVisitorsMo}
                onChange={(e) => setVolK("aeVisitorsMo", e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="grid two" style={{ marginTop: 12 }}>
          <div className="card">
            <h2>Known & Estimated Costs (for narrative tables)</h2>
            {[
              ["Retail CPL (syndication)", "cplRetail"],
              ["AD CPL (low)", "cplADLow"],
              ["AD CPL (high)", "cplADHigh"],
              ["Intel/Intent per Rep per Quarter", "intelIntentPerQuarter"],
              ["Account Plan per Play", "accountPlanPerPlay"],
              ["Content Support per Play", "contentSupportPerPlay"],
            ].map(([label, key]) => (
              <div className="input-row" key={key}>
                <label>{label}</label>
                <input
                  type="number"
                  value={costs[key]}
                  onChange={(e) => setCost(key, e.target.value)}
                />
              </div>
            ))}
          </div>

          <div className="card">
            <h2>Program Activity Levels</h2>
            {[
              ["Retail Syndication / Month", "retailSyndPerMo"],
              ["AD Syndication / Month", "adSyndPerMo"],
              ["Retail 1:1 Plays / Q", "retailOneToOnePerQ"],
              ["Retail 1:Few Plays / Q", "retailOneToFewPerQ"],
              ["AD 1:1 Plays / Rep / Q", "adOneToOnePerRepPerQ"],
              ["AD 1:Few Plays / Rep / Q", "adOneToFewPerRepPerQ"],
              ["AE 1:Few Plays / Rep / Q", "aeOneToFewPerRepPerQ"],
            ].map(([label, key]) => (
              <div className="input-row" key={key}>
                <label>{label}</label>
                <input
                  type="number"
                  value={costs[key]}
                  onChange={(e) => setCost(key, e.target.value)}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="table-block" style={{ marginTop: 12 }}>
          <div className="header-row">
            <div className="section-title">Services & Solutions Add-Ons</div>
          </div>
          <div className="small">
            Start empty; add only what you want included (PM/Admin, tooling,
            training, travel, events, etc.).
          </div>
          <table className="assump-table" style={{ marginTop: 8 }}>
            <thead>
              <tr>
                <th>Item</th>
                <th className="right">Amount</th>
                <th>Cadence</th>
              </tr>
            </thead>
            <tbody>
              {addons.length === 0 && (
                <tr>
                  <td colSpan="3" className="center small">
                    No add-ons yet.
                  </td>
                </tr>
              )}
              {addons.map((h, i) => (
                <tr key={i}>
                  <td>{h.label}</td>
                  <td className="right">{money(h.amount)}</td>
                  <td>{h.per}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  }

  /* ------------------ RENDER ------------------ */
  return (
    <div className="container">
      <style>{INLINE_CSS}</style>

      <div className="tabs">
        <button
          className={`tab-btn ${tab === "budget" ? "active" : ""}`}
          onClick={() => setTab("budget")}
        >
          STAR 2026 GTM Math – Budget & Forecast
        </button>
        <button
          className={`tab-btn ${tab === "forecast" ? "active" : ""}`}
          onClick={() => setTab("forecast")}
        >
          STAR 2026 GTM Math – 12 Month Forecast
        </button>
        <button
          className={`tab-btn ${tab === "assumptions" ? "active" : ""}`}
          onClick={() => setTab("assumptions")}
        >
          Assumptions & Scenarios
        </button>
      </div>

      {tab === "budget" && <TabBudget />}
      {tab === "forecast" && <TabForecast />}
      {tab === "assumptions" && <Assumptions />}

      {toast && (
        <div className="toast">
          <h4>{toast.title}</h4>
          <ul>
            {toast.items.map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
